"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { gsap } from "gsap"
import axios from "axios"
import validator from "validator"
import { API_BASE_URL } from "../utils/Constant"
import OtpModal from "./OtpModal"

const RegisterForm = () => {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneDigits, setPhoneDigits] = useState("") // Store only the 10 digits
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState("")
  const [emailError, setEmailError] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const [isEmailValidating, setIsEmailValidating] = useState(false)
  const [isMxValid, setIsMxValid] = useState(null) // null: unchecked, true: valid, false: invalid
  const [otp, setOtp] = useState("")
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [attemptsLeft, setAttemptsLeft] = useState(5)
  const navigate = useNavigate()

  const formRef = useRef(null)

  useEffect(() => {
    setIsMxValid(null)
  }, [email])

  useEffect(() => {
    const formElement = formRef.current
    const formChildren = formElement?.querySelectorAll(".form-item") || []
    gsap.set(formChildren, { y: 50, opacity: 0 })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(formChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            })
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (formElement) observer.observe(formElement)

    return () => {
      if (formElement) observer.unobserve(formElement)
    }
  }, [])

  const validatePassword = (password) => {
    const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/
    if (password.length < 8) {
      setPasswordStrength("Password must be at least 8 characters long.")
    } else if (!passwordValidationRegex.test(password)) {
      setPasswordStrength("Use at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., !@#$%^&*).")
    } else {
      setPasswordStrength("Password is strong!")
    }
  }

  const validateEmailFormat = (email) => {
    if (!validator.isEmail(email)) {
      setEmailError("Please enter a valid email address.")
      return false
    } else {
      setEmailError("")
      return true
    }
  }

  const validatePhoneNumber = (digits) => {
    const fullPhone = `+91${digits}`
    const startsWithValidDigit = /^[6-9]/.test(digits)
    if (!validator.isMobilePhone(fullPhone, "en-IN") || !startsWithValidDigit) {
      setPhoneError("Please enter a valid 10-digit Indian phone number starting with 6, 7, 8, or 9.")
      return false
    } else if (digits.length !== 10) {
      setPhoneError("Phone number must be exactly 10 digits.")
      return false
    } else {
      setPhoneError("")
      return true
    }
  }

  const checkMxRecords = async (email) => {
    setIsEmailValidating(true)
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/check-mx`, { email })
      if (response.status === 200 && response.data.hasMxRecords) {
        setIsMxValid(true)
        setEmailError("")
      } else {
        setIsMxValid(false)
        setEmailError("This email doesn’t seem to exist. Try another one.")
      }
    } catch (err) {
      setIsMxValid(false)
      setEmailError("We couldn’t verify this email. Please check it and try again.")
      console.error(`MX check failed: ${err.message}`)
    } finally {
      setIsEmailValidating(false)
    }
  }

  const handleEmailBlur = async (e) => {
    const emailValue = e.target.value
    setEmail(emailValue)
    if (validateEmailFormat(emailValue)) {
      await checkMxRecords(emailValue)
    }
  }

  const handlePhoneBlur = (e) => {
    const phoneValue = e.target.value
    setPhoneDigits(phoneValue)
    validatePhoneNumber(phoneValue)
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    if (!isMxValid || isEmailValidating) {
      setError("Please use a valid email address that can receive emails.")
      return
    }
    if (!validatePhoneNumber(phoneDigits)) {
      setError("Please enter a valid Indian phone number.")
      return
    }

    const phoneNumber = `+91${phoneDigits}`

    setIsLoading(true)
    setError("")

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        name,
        email,
        phone_number: phoneNumber, // Include phone number in the request
        password,
      })
      if (response.status === 200) {
        setIsOtpSent(true)
      } else {
        setError(response.data.error || "Something went wrong. Please try again.")
      }
    } catch (err) {
      setError(err.response?.data?.error || "Oops! Registration failed. Try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpError("Please enter a 6-digit OTP.")
      return
    }

    setIsLoading(true)
    setOtpError("")

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
        email,
        email_otp: otp, // Match backend expectation of "email_otp"
      })
      if (response.status === 201) {
        setIsOtpSent(false)
        navigate("/auth/login")
      } else {
        setOtpError(response.data.error || "Wrong OTP. Try again!")
        setAttemptsLeft((prev) => prev - 1)
        if (attemptsLeft <= 1) {
          setError("Too many wrong attempts. Please start over.")
          setIsOtpSent(false)
        }
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || "OTP verification failed. Try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsLoading(true)
    setOtpError("")

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email })
      if (response.status === 200) {
        setOtpError("New OTP sent! Check your email.")
      } else {
        setOtpError(response.data.error || "Couldn’t send OTP. Try again.")
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || "Failed to resend OTP. Try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-700 text-white overflow-hidden">
      <section className="relative z-10 min-h-screen flex items-center justify-center py-16 px-4 md:px-6 lg:px-8">
        <div ref={formRef} className="w-full max-w-md bg-slate-700/50 backdrop-blur-sm p-8 rounded-xl shadow-lg">
          <h2 className="form-item text-2xl md:text-3xl font-bold text-center mb-10">Create Account</h2>
          <form onSubmit={handleRegister} className="space-y-6">
            <div className="form-item">
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="form-item">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  validateEmailFormat(e.target.value)
                }}
                onBlur={handleEmailBlur}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {emailError && <p className="text-red-400 text-base mt-2">{emailError}</p>}
              {isEmailValidating && <p className="text-slate-400 text-base mt-2">Checking your email...</p>}
            </div>
            <div className="form-item">
              <div className="flex">
                <span className="inline-flex items-center px-4 py-4 text-lg bg-slate-900 text-white border border-r-0 border-slate-600 rounded-l-lg">
                  +91
                </span>
                <input
                  type="tel"
                  placeholder="10-digit number"
                  value={phoneDigits}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 10) // Only digits, max 10
                    setPhoneDigits(value)
                    validatePhoneNumber(value)
                  }}
                  onBlur={handlePhoneBlur}
                  required
                  className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {phoneError && <p className="text-red-400 text-base mt-2">{phoneError}</p>}
            </div>
            <div className="form-item">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  validatePassword(e.target.value)
                }}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {passwordStrength && (
                <p className={`text-base mt-2 ${passwordStrength === "Password is strong!" ? "text-green-400" : "text-red-400"}`}>
                  {passwordStrength}
                </p>
              )}
            </div>
            {error && <p className="form-item text-red-400 text-base">{error}</p>}
            <button
              type="submit"
              className={`form-item w-full py-4 px-8 text-lg font-semibold rounded-lg transition-colors ${
                isLoading || passwordStrength !== "Password is strong!" || emailError || phoneError || !isMxValid || isEmailValidating
                  ? "bg-slate-600 text-slate-300 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={isLoading || passwordStrength !== "Password is strong!" || emailError || phoneError || !isMxValid || isEmailValidating}
            >
              {isLoading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>

          {isOtpSent && (
            <OtpModal
              otp={otp}
              setOtp={setOtp}
              otpError={otpError}
              handleVerifyOtp={handleVerifyOtp}
              handleResendOtp={handleResendOtp}
            />
          )}
        </div>
      </section>
    </div>
  )
}

export default RegisterForm